import { TextImageContentModule } from '@silvertours/common-landingpages-view';
import { ReactNode } from 'react';
import { Ui } from '@silvertours/front-shared';

import {
  HasHeadingStyle,
  HasModuleContainerSpacingRules,
  HasTextStyleRules,
  Heading,
  LinkButton,
  ModuleContainer,
  RichText,
} from '../element';
import { HasColumns } from '../rule';
import { SectionTitle } from '../element/SectionTitle';
import { HasSectionTitleRules } from '../element/rule';
import {
  GridWrapper,
  FullWidthItem,
  LeftColumnItem,
  RightColumnItem,
  StyledAdditionalInfo,
  FullWidthHeadingItem,
} from './TextContentBlock.styles';

export type TextContentBlockRules = {
  disableSnippetStyles?: boolean;
  isContainerMultiColumn: boolean;
  isSecondColumn?: boolean;
} & HasSectionTitleRules &
  HasColumns &
  HasHeadingStyle &
  HasModuleContainerSpacingRules &
  HasTextStyleRules;

type TextContentBlockContent = Pick<
  TextImageContentModule,
  'badge' | 'heading' | 'content' | 'link'
>;

export type TextContentBlockProps = {
  content: TextContentBlockContent;
  rules?: TextContentBlockRules;
  additionalInfo?: {
    content: string;
    severity?: Ui.AdditionalInfoSeverity;
    customIcon?: ReactNode;
  };
};

export const TextContentBlock = ({
  content: { badge, heading, content, link },
  rules,
  additionalInfo,
}: TextContentBlockProps) => {
  const {
    sectionTitle: sectionTitleRules,
    columns,
    disableSnippetStyles,
    heading: headingStyle = {},
    isContainerMultiColumn,
    isSecondColumn,
    textStyle,
    verticalSpacing,
  } = rules || {};
  const { style } = textStyle || {};

  if (additionalInfo) {
    return (
      <ModuleContainer
        rules={{
          isSecondColumn,
          verticalSpacing,
        }}
      >
        <GridWrapper>
          <FullWidthItem>
            <SectionTitle
              content={badge}
              rules={{
                sectionTitle: sectionTitleRules,
                verticalSpacing: { isLargeAtMD: true },
              }}
            />
          </FullWidthItem>
          <FullWidthHeadingItem>
            <Heading
              content={heading}
              rules={{ textStyle: { style }, ...headingStyle }}
            />
          </FullWidthHeadingItem>
          <LeftColumnItem>
            {content && (
              <RichText
                content={content}
                rules={{ columns, disableSnippetStyles, textStyle: { style } }}
              />
            )}
            <LinkButton
              content={link}
              rules={{
                variant: 'secondary',
                size: 'small',
                horizontalSpacing: { isCentered: !isContainerMultiColumn },
              }}
            />
          </LeftColumnItem>
          <RightColumnItem>
            <StyledAdditionalInfo
              severity={additionalInfo.severity}
              customIcon={additionalInfo.customIcon}
            >
              {additionalInfo.content}
            </StyledAdditionalInfo>
          </RightColumnItem>
        </GridWrapper>
      </ModuleContainer>
    );
  }

  return (
    <ModuleContainer
      rules={{
        isSecondColumn,
        verticalSpacing,
      }}
    >
      <SectionTitle
        content={badge}
        rules={{
          sectionTitle: sectionTitleRules,
          verticalSpacing: { isLargeAtMD: true },
        }}
      />
      <Heading
        content={heading}
        rules={{ textStyle: { style }, ...headingStyle }}
      />
      {content && (
        <RichText
          content={content}
          rules={{ columns, disableSnippetStyles, textStyle: { style } }}
        />
      )}
      <LinkButton
        content={link}
        rules={{
          variant: 'secondary',
          size: 'small',
          horizontalSpacing: { isCentered: !isContainerMultiColumn },
        }}
      />
    </ModuleContainer>
  );
};
